import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Paper } from '@material-ui/core';
import { createElement, Dispatch, FC, Fragment, SetStateAction, useEffect, useRef, useState } from 'react';

import CreateModificationRequest from '../../../entity/CreateModificationRequest';
import EditModificationRequestItem from '../../../entity/EditModificationRequestItem';
import Language from '../../../entity/Language';
import ModificationRequestEditing from '../../../entity/ModificationRequestEditing';
import ProductPricing from '../../../entity/ProductPricing';
import UploadingFile from '../../../entity/UploadingFile';
import {
  CreationDetailFragment,
  ModificationRequestFeeType,
  ModificationRequestForDetailFragment,
  SingleDirectorUserFragment,
} from '../../../generated/graphql';
import { getLatestDeliverable } from '../../../graphql/creation';
import ModificationRequestAlreadySentCard from '../../component/compound/ModificationRequestAlreadySentCard';
import ModificationRequestCreatingCard from '../../component/compound/ModificationRequestCreatingCard';
import ModificationRequestDetail from '../../component/compound/ModificationRequestDetail';
import ModificationRequestStartCard from '../../component/compound/ModificationRequestStartCard';
import { SPACING_REGULAR } from '../../constant/spacing';

type Props = {
  organizationId: string;
  pointBalance: number | null;
  creation: CreationDetailFragment;
  isEditable: boolean;
  isSelectedLatestDeliverable: boolean;
  setEditable: Dispatch<SetStateAction<boolean>>;
  createModificationRequest: CreateModificationRequest;
  editModificationRequestItem: EditModificationRequestItem;
  modificationRequestEditing: ModificationRequestEditing;
  assets: Array<FileReference>;
  modificationRequest?: ModificationRequestForDetailFragment;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  language: Language;
  email: string;
  onClickRequestStart: () => Promise<void>;
  onSelectFeeType: (feeType: ModificationRequestFeeType) => void;
  onPushInputtingItem: (
    modificationRequestId: string,
    bucketId: string | null,
    inputDuration: string,
    inputComment: string,
    files: Array<FileReference>,
  ) => Promise<void>;
  onUploadModificationRequestAssetFiles: (files: UploadingFile[]) => void;
  onCancelModificationrequestEditing: () => Promise<void>;
  onUploadIssueAssetFiles: (files: UploadingFile[], bucketId: string, itemId: string) => void;
  onUploadIssueCommentAssetFiles: (files: UploadingFile[], deliverableId: string, issueId: string) => void;
  onRemoveModificationRequestItem: (modificationRequestItemId: string, index: number) => void;
  onRemoveAssetFile: (bucketId: string, fileId: string) => void;
  onRemoveAllAssetFile: () => void;
  onRemoveAssetIssueFile: (bucketId: string, fileId: string, issueId: string) => void;
  onRemoveAssetIssueCommentFile: (bucketId: string, fileId: string, issueId: string) => void;
  onRemoveEditAssetFile: (fileId: string) => void;
  onOpenEditModificationRequestDialog: (index: number) => void;
  onCloseEditModificationRequestDialog: () => void;
  onSubmitEditModificationRequestDialog: (
    index: number,
    editModificationRequestItem: EditModificationRequestItem,
    modificationRequestItemId: string,
    duration: string,
    comment: string,
  ) => void;
  onRequestEditAssetFiles: (files: UploadingFile[], bucketId: string) => void;
  onRequestSendModificationRequest: () => Promise<void>;
  onSubmitModificationRequestIssue: (
    modificationRequestItemId: string,
    inputComment: string,
    bucketId: string | null,
    files: Array<FileReference>,
  ) => Promise<void>;
  onSubmitModificationRequestIssueComment: (issueId: string, comment: string) => void;
  onRequestResolveModificationRequestIssue: (id: string) => void;
  onRequestReopenModificationRequestIssue: (id: string) => void;
  onRefetchCreation: () => void;
  onPurchasedPoint: (point: number) => Promise<void>;
  prepareUser: () => Promise<SingleDirectorUserFragment | void>;
  className?: string;
};

const ModificationRequestCard: FC<Props> = ({
  organizationId,
  pointBalance,
  creation,
  isEditable,
  setEditable,
  isSelectedLatestDeliverable,
  createModificationRequest,
  editModificationRequestItem,
  modificationRequestEditing,
  assets,
  modificationRequest,
  isPointRequired,
  productPricing,
  language,
  email,
  onPurchasedPoint,
  prepareUser,
  onClickRequestStart,
  onSelectFeeType,
  onPushInputtingItem,
  onUploadModificationRequestAssetFiles,
  onCancelModificationrequestEditing,
  onUploadIssueAssetFiles,
  onUploadIssueCommentAssetFiles,
  onRemoveModificationRequestItem,
  onRemoveAssetFile,
  onRemoveAllAssetFile,
  onRemoveAssetIssueFile,
  onRemoveAssetIssueCommentFile,
  onRemoveEditAssetFile,
  onOpenEditModificationRequestDialog,
  onCloseEditModificationRequestDialog,
  onSubmitEditModificationRequestDialog,
  onRequestEditAssetFiles,
  onRequestSendModificationRequest,
  onSubmitModificationRequestIssue,
  onSubmitModificationRequestIssueComment,
  onRequestResolveModificationRequestIssue,
  onRequestReopenModificationRequestIssue,
  onRefetchCreation,
  className,
}) => {
  const [draftModificationRequestDeliveryDue, setDraftModificationRequestDeliveryDue] = useState<Date | undefined>(
    undefined,
  );

  useEffect(() => {
    setDraftModificationRequestDeliveryDue(createModificationRequest.deliveryDue!);
  }, [createModificationRequest]);

  const AlreadySent =
    modificationRequest && /(submitted|responded)/.test(modificationRequest.state) ? (
      <ModificationRequestAlreadySentCard modificationRequestState={modificationRequest.state} />
    ) : null;

  const modificationRequestProcessing = useRef(false);
  const Start = !isEditable ? (
    !modificationRequest || (modificationRequest && modificationRequest.state === 'given_feedback') ? (
      <ModificationRequestStartCard
        creation={creation}
        isSelectedLatestDeliverable={isSelectedLatestDeliverable}
        modificationRequestState={modificationRequest ? modificationRequest.state : null}
        onClickRequestButton={async () => {
          if (modificationRequestProcessing.current) return;
          modificationRequestProcessing.current = true;
          await onClickRequestStart().then(() => setEditable(true));
          modificationRequestProcessing.current = false;
        }}
        isPointRequired={isPointRequired}
      />
    ) : null
  ) : null;

  const targetDeliverable = modificationRequest
    ? creation.deliverables.find((deliverable) => deliverable.id === modificationRequest.targetDeliverableId)
    : undefined;

  const Editor = isEditable ? (
    <ModificationRequestCreatingCard
      creation={creation}
      createModificationRequest={createModificationRequest}
      editModificationRequestItem={editModificationRequestItem}
      assetFiles={assets}
      organizationId={organizationId}
      language={language}
      email={email}
      onPurchasedPoint={onPurchasedPoint}
      pointBalance={pointBalance}
      isPointRequired={isPointRequired}
      productPricing={productPricing}
      onSelectFeeType={onSelectFeeType}
      onUploadAssetFiles={onUploadModificationRequestAssetFiles}
      onPushInputtingItem={onPushInputtingItem}
      onRemoveEditAssetFile={onRemoveEditAssetFile}
      onOpenEditModificationRequestDialog={onOpenEditModificationRequestDialog}
      onCloseEditModificationRequestDialog={onCloseEditModificationRequestDialog}
      onSubmitEditModificationRequestDialog={onSubmitEditModificationRequestDialog}
      onRequestEditAssetFiles={onRequestEditAssetFiles}
      onRequestRemoveItem={onRemoveModificationRequestItem}
      onRemoveAssetFile={onRemoveAssetFile}
      onRemoveAllAssetFile={onRemoveAllAssetFile}
      onClickCancelButton={async () => {
        await onCancelModificationrequestEditing().then(() => setEditable(false));
      }}
      onClickSendButton={onRequestSendModificationRequest}
      onRefetchCreation={onRefetchCreation}
      prepareUser={prepareUser}
      draftModificationRequestDeliveryDue={draftModificationRequestDeliveryDue}
    />
  ) : null;

  const Detail =
    modificationRequest && modificationRequest.state.match(/submitted|responded|given_feedback|approved/) ? (
      <ModificationRequestDetail
        modificationRequestExpired={!!creation.modificationRequestExpired}
        modificationRequest={modificationRequest}
        modificationRequestEditing={modificationRequestEditing}
        deliverable={targetDeliverable}
        assetFiles={assets}
        onUploadIssueAssetFiles={onUploadIssueAssetFiles}
        onUploadIssueCommentAssetFiles={onUploadIssueCommentAssetFiles}
        onRemoveAssetIssueFile={onRemoveAssetIssueFile}
        onRemoveAssetIssueCommentFile={onRemoveAssetIssueCommentFile}
        onSubmitModificationRequestIssueComment={onSubmitModificationRequestIssueComment}
        onRemoveAllAssetFile={onRemoveAllAssetFile}
        onSubmitModificationRequestIssue={onSubmitModificationRequestIssue}
        onRequestResolveModificationRequestIssue={onRequestResolveModificationRequestIssue}
        onRequestReopenModificationRequestIssue={onRequestReopenModificationRequestIssue}
      />
    ) : null;

  return creation && getLatestDeliverable(creation) ? (
    <Root className={className}>
      <>
        {AlreadySent}
        {Start}
        {Editor}
        {Detail}
      </>
    </Root>
  ) : null;
};

const Root = styled(Paper)({
  padding: SPACING_REGULAR,
});

export default ModificationRequestCard;
